import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFirebase } from '../../context/FirebaseContext';
import { useTheme } from '../../context/ThemeContext';
import { useConceptGate } from '../../context/ConceptGateContext';
import { useFeatureFlags, useCryptoEnabled, useStocksEnabled, useJournalPageEnabled, useAnalyticsEnabled } from '../../context/FeatureFlagContext';
import { useAnalytics } from '../../context/AnalyticsContext';
import LeftPanel from './LeftPanel/LeftPanel';
import { usePanelState } from './LeftPanel/panelState';
import MiddlePanel from './MiddlePanel/MiddlePanel';
import OnboardingTour from './components/OnboardingTour';
import './CopilotMainView.css';
import Header from './Header/Header';
import { deleteChatV2, getUserChatsV2, postCreateChatV2 } from '../../api/chat';
import { searchStocksV2 } from '../../api/stock';
import Chat from './RightPanel/Chat';
import logger from '../../utils/logger';
import { getInstrumentHistory, addToInstrumentHistory } from '../../utils/instrumentHistory';
import { analytics } from '../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { isCodexUi } from '../../utils/codexEnv';
import { useLayoutState } from './hooks/useLayoutState';
import ResizeHandle from './RightPanel/ResizeHandle';
import FloatingChatWindow from './RightPanel/FloatingChatWindow';
import KeyboardShortcutsHelp from './components/KeyboardShortcutsHelp';
import RulesHelpModal from './ConceptGatePanel/RulesHelpModal';
import MobileView from '../MobileView/MobileView';
import PanelHeader from '../MobileView/components/PanelHeader';
import InstrumentSearch from '../MobileView/components/InstrumentSearch';
import ChatHistoryList from '../MobileView/components/ChatHistoryList';
import InstrumentHistoryList from '../MobileView/components/InstrumentHistoryList';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import TraderJournalView from '../TraderJournalView/TraderJournalView';
import { ConceptGatePanel } from './ConceptGatePanel';
import MobileGateOverlay from '../MobileView/MobileGateOverlay';
import ValidationHistoryView from './ConceptGatePanel/ValidationHistoryView';

// Custom hook for mobile view detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const NTBChatView = () => {
  const navigate = useNavigate();
  const { logOut, auth } = useFirebase();
  const panelState = usePanelState();
  const isExpandedLeftPanel = panelState.visible && !panelState.collapsed;
  const { isDarkMode, toggleTheme } = useTheme();
  const userInfo = useSelector(state => state.user.userInfo);
  const isAdmin = useSelector(state => state.user.isAdmin);
  const chartRef = useRef(null);
  const onboardingTourRef = useRef(null);
  const isMobile = useIsMobile();
  const { layoutState, setRightPanelWidth, setRightPanelDetached, setFloatingPosition } = useLayoutState();
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);

  // Journal visibility state
  const [journalVisible, setJournalVisible] = useState(false);
  const toggleJournal = useCallback(() => setJournalVisible(prev => !prev), []);

  // History view visibility state
  const [historyViewVisible, setHistoryViewVisible] = useState(false);
  const showHistoryView = useCallback(() => setHistoryViewVisible(true), []);
  const hideHistoryView = useCallback(() => setHistoryViewVisible(false), []);

  // Current price from chart (for sample trade generation)
  const [chartCurrentPrice, setChartCurrentPrice] = useState(null);

  // Feature flags
  const { chat: chatEnabled } = useFeatureFlags();
  const cryptoEnabled = useCryptoEnabled();
  const stocksEnabled = useStocksEnabled();
  const journalPageEnabled = useJournalPageEnabled();
  const analyticsEnabled = useAnalyticsEnabled();

  // Analytics context - sync instrument
  const { setSelectedInstrument: setAnalyticsInstrument } = useAnalytics();

  // ConceptGate state
  const {
    panelVisible: conceptGatePanelVisible,
    togglePanel: toggleConceptGatePanel,
    panelWidth: conceptGatePanelWidth,
    setPanelWidth: setConceptGatePanelWidth,
    resetConceptGateState,
    setSelectedInstrument: setConceptGateInstrument,
    setTradeSpec,
    subscriptionStatus
  } = useConceptGate();

  // Chat panel visibility state - respect feature flag
  const [chatPanelVisible, setChatPanelVisible] = useState(() => {
    if (!chatEnabled) return false; // Force hidden if chat feature disabled
    const savedState = localStorage.getItem('ntb:copilot:chatPanelVisible');
    return savedState ? JSON.parse(savedState) : true;
  });

  useEffect(() => {
    if (!isMobile && isMobilePanelOpen) {
      setIsMobilePanelOpen(false);
    }
  }, [isMobile, isMobilePanelOpen]);

  // Sync chat visibility with feature flag
  useEffect(() => {
    if (!chatEnabled) {
      // Chat disabled - force hide
      if (chatPanelVisible) {
        setChatPanelVisible(false);
      }
    } else if (!chatPanelVisible) {
      // Chat enabled but panel hidden - restore user's saved preference
      const savedState = localStorage.getItem('ntb:copilot:chatPanelVisible');
      const shouldShow = savedState !== null ? JSON.parse(savedState) : true;
      if (shouldShow) {
        setChatPanelVisible(true);
      }
    }
  }, [chatEnabled, chatPanelVisible]);


  const handleDetachChat = useCallback(() => {
    // Save current chat width to floating position before detaching
    const currentWidth = layoutState.rightPanel.width;
    // Position window on the right side by default
    const defaultX = window.innerWidth - (currentWidth || 400) - 20; // 20px margin from right edge
    const defaultY = 100; // 100px from top
    const defaultHeight = Math.min(window.innerHeight - 200, 600); // Max height with margins

    setFloatingPosition({
      x: defaultX,
      y: defaultY,
      width: currentWidth || 400,
      height: defaultHeight
    });
    setRightPanelDetached(true);
  }, [setRightPanelDetached, layoutState.rightPanel.width, setFloatingPosition]);

  const handleDockChat = useCallback(() => {
    // Restore chat width from floating window size when docking
    const floatingWidth = layoutState.rightPanel.floatingPosition.width;
    if (floatingWidth && floatingWidth >= 250 && floatingWidth <= 800) {
      setRightPanelWidth(floatingWidth);
    }
    setRightPanelDetached(false);
  }, [setRightPanelDetached, layoutState.rightPanel.floatingPosition.width, setRightPanelWidth]);

  const handleFloatingPositionChange = useCallback((position) => {
    setFloatingPosition(position);
  }, [setFloatingPosition]);

  const handleFloatingSizeChange = useCallback((size) => {
    setFloatingPosition({ width: size.width, height: size.height });
  }, [setFloatingPosition]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + B: Toggle left panel
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        panelState.toggle();
      }

      // Cmd/Ctrl + Shift + D: Toggle chat detach (only when chat is enabled)
      if (chatEnabled && (e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        if (layoutState.rightPanel.detached) {
          handleDockChat();
        } else {
          handleDetachChat();
        }
      }

      // Esc: Dock floating chat if detached (only when chat is enabled)
      if (chatEnabled && e.key === 'Escape' && layoutState.rightPanel.detached) {
        handleDockChat();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [chatEnabled, layoutState.rightPanel.detached, handleDetachChat, handleDockChat, panelState]);

  const [selectedCategory, setSelectedCategory] = useState(() => {
    const saved = localStorage.getItem('ntb:copilot:selectedCategory');
    return saved || 'FOREX';
  });
  const [selectedInstrument, setSelectedInstrument] = useState(() => {
    return localStorage.getItem('ntb:copilot:selectedInstrument') || '';
  });
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem('ntb:copilot:selectedInstrument') || '';
  });
  const [autoCompleteOptions, setAutoCompleteOptions] = useState([]);
  const [recentChats, setRecentChats] = useState([]);
  const [recentInstruments, setRecentInstruments] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [isChatExpanded, setIsChatExpanded] = useState(() => {
    const savedState = localStorage.getItem('ntb:copilot:isChatExpanded');
    return savedState ? JSON.parse(savedState) : false;
  });
  const [activeTab, setActiveTab] = useState('graph');
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  const [isMobileAccountMenuOpen, setIsMobileAccountMenuOpen] = useState(false);
  const [isInstrumentLoading, setIsInstrumentLoading] = useState(false);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [shouldHighlightInstrumentSelector, setShouldHighlightInstrumentSelector] = useState(false);
  const [isInstrumentSearchActive, setIsInstrumentSearchActive] = useState(false);
  const [isKeyboardShortcutsVisible, setIsKeyboardShortcutsVisible] = useState(false);
  const [isRulesHelpVisible, setIsRulesHelpVisible] = useState(false);
  const [isPanelsSwitched, setIsPanelsSwitched] = useState(() => {
    const savedState = localStorage.getItem('ntb:copilot:isPanelsSwitched');
    return savedState ? JSON.parse(savedState) : false;
  });

  // Persist instrument selection to localStorage
  useEffect(() => {
    if (selectedInstrument) {
      localStorage.setItem('ntb:copilot:selectedInstrument', selectedInstrument);
      localStorage.setItem('ntb:copilot:selectedCategory', selectedCategory);
    }
  }, [selectedInstrument, selectedCategory]);

  useEffect(() => {
    if (selectedInstrument) {
      setConceptGateInstrument(selectedInstrument, selectedCategory);
      // Also sync with analytics context
      if (analyticsEnabled) {
        setAnalyticsInstrument(selectedInstrument);
      }
    }
  }, [selectedInstrument, selectedCategory, setConceptGateInstrument, setAnalyticsInstrument, analyticsEnabled]);

  // Reset state when user changes (to prevent data leak between users)
  useEffect(() => {
    // Reset chat-related state
    setSelectedChatId(null);
    setRecentChats([]);
    setAutoCompleteOptions([]);
    setSearchQuery(localStorage.getItem('ntb:copilot:selectedInstrument') || '');
    // Load instrument history for this user
    if (auth?.currentUser?.uid) {
      setRecentInstruments(getInstrumentHistory(auth.currentUser.uid));
    } else {
      setRecentInstruments([]);
    }
  }, [auth?.currentUser?.uid]);

  const toggleChatPanel = useCallback(() => {
    // Don't allow toggling if chat feature is disabled
    if (!chatEnabled) return;

    const newState = !chatPanelVisible;
    setChatPanelVisible(newState);
    localStorage.setItem('ntb:copilot:chatPanelVisible', JSON.stringify(newState));

    // If hiding chat and currently on chat tab (mobile), switch to graph tab
    if (!newState && isMobile && activeTab === 'chat') {
      setActiveTab('graph');
    }

    if (!isCodexUi && analytics) {
      logEvent(analytics, 'chat_panel_visibility_toggled', {
        visible: newState,
        chat_id: selectedChatId,
        instrument: selectedInstrument
      });
    }
  }, [chatEnabled, chatPanelVisible, selectedChatId, selectedInstrument, isMobile, activeTab]);

  // Extract userId for dependencies
  const userId = auth?.currentUser?.uid;

  const fetchRecentChats = useCallback(async () => {
    if (!userId) {
      setRecentChats([]);
      return;
    }
    await getUserChatsV2(auth, (response) => {
      if (response && !response.error) {
        setRecentChats(response);
      } else {
        logger.error('Failed to fetch recent chats:', response?.error);
        setRecentChats([]); // Clear on error too
      }
    });
  }, [auth, userId]);

  useEffect(() => {
    fetchRecentChats();
  }, [fetchRecentChats]);

  const handleDeleteChat = async (chatId, chatName) => {
    if (isDeletingChat) {
      return;
    }

    setIsDeletingChat(true);

    try {
      await deleteChatV2(auth, (response) => {
        if (response && !response.error) {
          setRecentChats(prev => prev.filter(chat => chat.chat_id !== chatId));
          if (selectedChatId === chatId) {
            setSelectedChatId(null);
          }

          fetchRecentChats();
          if (!isCodexUi && analytics) {
            logEvent(analytics, 'chat_deleted', {
              chat_id: chatId,
              chat_name: chatName
            });
          }
        } else {
          logger.error('Failed to delete chat:', response?.error);
        }
      }, chatId);
    } catch (error) {
      logger.error('Error deleting chat:', error);
    } finally {
      setIsDeletingChat(false);
    }
  };

  // Add auth check effect
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setAutoCompleteOptions([]);
      return;
    }

    const shouldFetchStocks = selectedCategory === 'STOCKS' || isInstrumentSearchActive;
    if (!shouldFetchStocks) {
      setAutoCompleteOptions([]);
      return;
    }

    const delay = isInstrumentSearchActive ? 500 : 300;
    const timer = setTimeout(() => {
      searchStocksV2(auth, (response) => {
        if (response && response.stocks && Array.isArray(response.stocks)) {
          const options = response.stocks.map(stock => ({
            value: stock.symbol,
            label: stock.symbol,
            provider: stock.provider,
            data: stock
          }));
          setAutoCompleteOptions(options);
        } else {
          setAutoCompleteOptions([]);
        }
      }, searchQuery);
    }, delay);

    return () => clearTimeout(timer);
  }, [auth, isInstrumentSearchActive, searchQuery, selectedCategory]);

  // Reorganize instruments into categories - filter based on feature flags
  const INSTRUMENTS = useMemo(() => {
    const forexInstruments = {
      EUR_USD: { label: "EUR/USD", pipValue: 0.0001, spread: 1.5, provider: "OANDA" },
      GBP_USD: { label: "GBP/USD", pipValue: 0.0001, spread: 2.0, provider: "OANDA" },
      USD_JPY: { label: "USD/JPY", pipValue: 0.01, spread: 1.4, provider: "OANDA" },
      AUD_USD: { label: "AUD/USD", pipValue: 0.0001, spread: 1.4, provider: "OANDA" },
      USD_CAD: { label: "USD/CAD", pipValue: 0.0001, spread: 2.2, provider: "OANDA" },
      EUR_GBP: { label: "EUR/GBP", pipValue: 0.0001, spread: 1.7, provider: "OANDA" },
      USD_CHF: { label: "USD/CHF", pipValue: 0.0001, spread: 1.8, provider: "OANDA" },
      NZD_USD: { label: "NZD/USD", pipValue: 0.0001, spread: 1.7, provider: "OANDA" },
      EUR_JPY: { label: "EUR/JPY", pipValue: 0.01, spread: 1.8, provider: "OANDA" },
      GBP_JPY: { label: "GBP/JPY", pipValue: 0.01, spread: 3.1, provider: "OANDA" },
    };

    const result = {
      FOREX: forexInstruments,
      INDICES: {
        NAS100_USD: { label: "NASDAQ100/USD", pipValue: 0.0001, spread: 1.7, provider: "OANDA" },
        SPX500_USD: { label: "S&P500/USD", pipValue: 0.0001, spread: 1.7, provider: "OANDA" },
        GER40_EUR: { label: "GER40/EUR", pipValue: 0.0001, spread: 1.7, provider: "OANDA" }
      }
    };

    // Commodities - always available (via Oanda)
    result.COMMODITIES = {
      XAU_USD: { label: "XAU/USD", pipValue: 0.01, spread: 3.0, provider: "OANDA" }
    };

    // Add crypto if enabled
    if (cryptoEnabled) {
      result.CRYPTO = {
        BTCUSDT: { label: "BTC/USDT", pipValue: 0.01, spread: 0.1, provider: "BINANCE" },
        ETHUSDT: { label: "ETH/USDT", pipValue: 0.01, spread: 0.1, provider: "BINANCE" },
        SOLUSDT: { label: "SOL/USDT", pipValue: 0.01, spread: 0.1, provider: "BINANCE" },
        BNBUSDT: { label: "BNB/USDT", pipValue: 0.01, spread: 0.1, provider: "BINANCE" },
        ADAUSDT: { label: "ADA/USDT", pipValue: 0.0001, spread: 0.1, provider: "BINANCE" },
        DOGEUSDT: { label: "DOGE/USDT", pipValue: 0.00001, spread: 0.1, provider: "BINANCE" },
        XRPUSDT: { label: "XRP/USDT", pipValue: 0.0001, spread: 0.1, provider: "BINANCE" },
        DOTUSDT: { label: "DOT/USDT", pipValue: 0.001, spread: 0.1, provider: "BINANCE" },
        MATICUSDT: { label: "MATIC/USDT", pipValue: 0.0001, spread: 0.1, provider: "BINANCE" },
        LINKUSDT: { label: "LINK/USDT", pipValue: 0.001, spread: 0.1, provider: "BINANCE" },
        AVAXUSDT: { label: "AVAX/USDT", pipValue: 0.01, spread: 0.1, provider: "BINANCE" },
        ATOMUSDT: { label: "ATOM/USDT", pipValue: 0.001, spread: 0.1, provider: "BINANCE" },
        LTCUSDT: { label: "LTC/USDT", pipValue: 0.01, spread: 0.1, provider: "BINANCE" },
        UNIUSDT: { label: "UNI/USDT", pipValue: 0.001, spread: 0.1, provider: "BINANCE" }
      };
    }

    // Add stocks if enabled
    if (stocksEnabled) {
      result.STOCKS = {
        AAPL: { label: "AAPL", pipValue: 0.01, spread: 0.1, provider: "YAHOO" },
        TNA: { label: "TNA", pipValue: 0.01, spread: 0.1, provider: "YAHOO" },
        TZA: { label: "TZA", pipValue: 0.01, spread: 0.1, provider: "YAHOO" },
        TSLA: { label: "TSLA", pipValue: 0.01, spread: 0.1, provider: "YAHOO" },
        MSFT: { label: "MSFT", pipValue: 0.01, spread: 0.1, provider: "YAHOO" },
        AMZN: { label: "AMZN", pipValue: 0.01, spread: 0.1, provider: "YAHOO" },
        GOOGL: { label: "GOOGL", pipValue: 0.01, spread: 0.1, provider: "YAHOO" }
      };
    }

    return result;
  }, [cryptoEnabled, stocksEnabled]);

  const handleLogout = async () => {
    try {
      // Clear ConceptGate state immediately
      if (resetConceptGateState) {
        resetConceptGateState();
      }

      await logOut();
      navigate('/');
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  };

  const togglePanel = () => {
    panelState.toggle();
  };

  // Update right panel width in CSS with debouncing for performance
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const rightPanel = document.querySelector('.right-panel');
      if (rightPanel) {
        // Use the actual width from layout state, no forced minimum
        rightPanel.style.setProperty('--right-panel-width', `${layoutState.rightPanel.width}px`);
      }
    }, 16); // ~60fps debounce

    return () => clearTimeout(timeoutId);
  }, [layoutState.rightPanel.width]);

  const handleInstrumentSelect = async (value, categoryOverride = selectedCategory) => {
    logger.info('Instrument selected:', { value: value });
    setIsInstrumentLoading(true);
    if (categoryOverride && categoryOverride !== selectedCategory) {
      setSelectedCategory(categoryOverride);
    }
    setSelectedInstrument(value);
    setSearchQuery(value);
    setShouldHighlightInstrumentSelector(false); // Clear highlight when instrument is selected

    // If value is empty, it means the user has cleared the selection
    if (!value) {
      setIsInstrumentLoading(false);
      return;
    }

    const activeCategory = categoryOverride || selectedCategory;
    // For stock tickers, create a default instrument object if it doesn't exist
    const isCustomTicker = activeCategory === 'STOCKS' &&
      !Object.keys(INSTRUMENTS[activeCategory]).includes(value);

    if (isCustomTicker) {
      // Add the custom ticker to the instruments object
      INSTRUMENTS.STOCKS[value] = {
        label: value,
        pipValue: 0.01,
        spread: 0.1,
        provider: "YAHOO"
      };
    }

    if (!isCodexUi && analytics) {
      logEvent(analytics, 'instrument_selected', {
        instrument: value,
        category: activeCategory,
        provider: INSTRUMENTS[activeCategory]?.[value]?.provider || 'unknown',
        previous_instrument: selectedInstrument,
        custom_ticker: isCustomTicker
      });
    }

    // When chat is disabled, use instrument history instead of chat sessions
    if (!chatEnabled) {
      const label = INSTRUMENTS[activeCategory]?.[value]?.label || value.replace('_', '/');
      const userId = auth?.currentUser?.uid;
      const updatedHistory = addToInstrumentHistory(userId, value, activeCategory, label);
      setRecentInstruments(updatedHistory);
      setIsInstrumentLoading(false);
      return;
    }

    // Find matching chat in recent chats
    const matchingChat = recentChats.find(chat => chat.chat_name === value);
    if (matchingChat) {
      setSelectedChatId(matchingChat.chat_id);
      setIsInstrumentLoading(false);
      return;
    }

    // Optimistic UI: Create temporary chat entry immediately
    const tempChatId = `temp-${Date.now()}`;
    const optimisticChat = {
      chat_id: tempChatId,
      chat_name: value,
      created_at: new Date().toISOString(),
      provider: INSTRUMENTS[activeCategory]?.[value]?.provider || 'YAHOO',
      category: activeCategory,
      ticker: value,
      isOptimistic: true
    };

    // Add to recent chats immediately (optimistic update)
    setRecentChats(prev => [optimisticChat, ...prev]);
    setSelectedChatId(tempChatId);

    // Create a new chat when instrument is selected
    const provider = INSTRUMENTS[activeCategory]?.[value]?.provider || 'YAHOO';
    const ticker = value;

    try {
      await postCreateChatV2(auth, async (response) => {
        if (response && !response.error) {
          // Remove optimistic chat and add real one
          setRecentChats(prev => {
            const filtered = prev.filter(chat => chat.chat_id !== tempChatId);
            return [response, ...filtered];
          });

          // Select the real chat
          if (response.chat_id) {
            setSelectedChatId(response.chat_id);
          }

          // Refresh to ensure consistency
          await fetchRecentChats();
        } else {
          logger.error('Failed to create new chat:', response?.error);
          // Remove optimistic chat on error
          setRecentChats(prev => prev.filter(chat => chat.chat_id !== tempChatId));
          setSelectedChatId(null);
        }
        setIsInstrumentLoading(false);
      }, value, provider, activeCategory, ticker);
    } catch (error) {
      logger.error('Error creating chat:', error);
      // Remove optimistic chat on error
      setRecentChats(prev => prev.filter(chat => chat.chat_id !== tempChatId));
      setSelectedChatId(null);
      setIsInstrumentLoading(false);
    }
  };

  const handleChatSelect = (chat) => {
    // First determine which category the instrument belongs to
    let instrumentCategory = null;
    let matched = false;

    // Check each category to find where the instrument belongs
    for (const [category, instruments] of Object.entries(INSTRUMENTS)) {
      if (Object.keys(instruments).some(key => key === chat.chat_name.replace('/', '_'))) {
        instrumentCategory = category;
        matched = true;
        break;
      }
    }

    // If no category matched and the chat name looks like a stock ticker, set to STOCKS
    if (!matched && /^[A-Z]+$/.test(chat.chat_name)) {
      instrumentCategory = 'STOCKS';

      // Create a default instrument object for this custom ticker
      if (!Object.keys(INSTRUMENTS.STOCKS).includes(chat.chat_name)) {
        INSTRUMENTS.STOCKS[chat.chat_name] = {
          label: chat.chat_name,
          pipValue: 0.01,
          spread: 0.1,
          provider: "YAHOO"
        };
      }
    }

    // If we found a matching category, set it first
    if (instrumentCategory) {
      setSelectedCategory(instrumentCategory);
    }

    // Set the selected chat ID
    setSelectedChatId(chat.chat_id);
    // Then select the instrument
    handleInstrumentSelect(chat.chat_name);
  };

  const handleCategoryChange = (category) => {
    setIsCategoryLoading(true);
    setSelectedCategory(category);
    setSelectedInstrument('');
    setSearchQuery('');
    setAutoCompleteOptions([]);
    setShouldHighlightInstrumentSelector(false); // Clear highlight when category changes

    if (!isCodexUi && analytics) {
      logEvent(analytics, 'category_changed', {
        category: category,
        previous_category: selectedCategory
      });
    }

    // Clear loading state after a short delay to allow UI to update
    setTimeout(() => {
      setIsCategoryLoading(false);
    }, 300);
  };

  const handleDisabledInputClick = () => {
    setShouldHighlightInstrumentSelector(true);

    if (isMobile) {
      setIsMobilePanelOpen(true);
    }

    // Log the event
    if (!isCodexUi && analytics) {
      logEvent(analytics, 'disabled_chat_input_clicked', {
        selected_category: selectedCategory
      });
    }

    // Clear highlight after animation duration (2 seconds for pulse)
    setTimeout(() => {
      setShouldHighlightInstrumentSelector(false);
    }, 2000);
  };

  const toggleChat = () => {
    const newState = !isChatExpanded;
    setIsChatExpanded(newState);
    localStorage.setItem('ntb:copilot:isChatExpanded', JSON.stringify(newState));

    if (!isCodexUi && analytics) {
      logEvent(analytics, 'chat_panel_toggled', {
        state: newState ? 'expanded' : 'collapsed',
        previous_state: isChatExpanded ? 'expanded' : 'collapsed',
        chat_id: selectedChatId,
        instrument: selectedInstrument
      });
    }
  };

  const togglePanelsSwitch = () => {
    const newState = !isPanelsSwitched;
    setIsPanelsSwitched(newState);
    localStorage.setItem('ntb:copilot:isPanelsSwitched', JSON.stringify(newState));

    if (!isCodexUi && analytics) {
      logEvent(analytics, 'panels_switched', {
        state: newState ? 'switched' : 'normal',
        previous_state: isPanelsSwitched ? 'switched' : 'normal',
        chat_id: selectedChatId,
        instrument: selectedInstrument
      });
    }
  };

  const addLine = (line) => {
    if (chartRef.current) {
      chartRef.current.addLine(line);
    }
  };

  const getMobileTabClass = () => {
    switch (activeTab) {
      case 'menu':
        return 'active-tab-menu';
      case 'graph':
        return 'active-tab-graph';
      case 'chat':
        return 'active-tab-chat';
      case 'settings':
        return 'active-tab-settings';
      default:
        return '';
    }
  };

  const handleTabChange = (tab) => {
    // Prevent switching to chat tab if chat panel is hidden
    if (tab === 'chat' && !chatPanelVisible) {
      return;
    }

    setActiveTab(tab);

    if (!isCodexUi && analytics) {
      logEvent(analytics, 'mobile_tab_changed', {
        tab: tab,
        previous_tab: activeTab,
        chat_id: selectedChatId,
        instrument: selectedInstrument,
        category: selectedCategory
      });
    }

    switch (tab) {
      case 'menu':
        panelState.setCollapsed(false);
        setIsChatExpanded(false);
        break;
      case 'chat':
        setIsChatExpanded(true);
        panelState.setCollapsed(true);
        break;
      case 'graph':
        setIsChatExpanded(false);
        panelState.setCollapsed(true);
        break;
      case 'validate':
        setIsChatExpanded(false);
        panelState.setCollapsed(true);
        break;
      case 'settings':
        // Handle settings tab
        break;
      default:
        setIsChatExpanded(false);
        panelState.setCollapsed(true);
    }
  };

  const getInstrumentLabel = () => {
    if (!selectedInstrument) {
      return selectedCategory;
    }

    return (
      INSTRUMENTS[selectedCategory]?.[selectedInstrument]?.label ||
      selectedInstrument.replace('_', '/')
    );
  };

  const selectedChat = recentChats.find(chat => chat.chat_id === selectedChatId);
  const chatTitle = selectedChat?.chat_name ? selectedChat.chat_name.replace('_', '/') : 'Chat';
  const mobileHeaderTitle = activeTab === 'chat' ? chatTitle : getInstrumentLabel();

  const handleMobileViewChange = (tab) => {
    handleTabChange(tab);
    setIsMobilePanelOpen(false);
    setIsMobileAccountMenuOpen(false);
  };

  const handleMobileInstrumentSelect = (value, category) => {
    handleInstrumentSelect(value, category);
    setIsMobilePanelOpen(false);
    setIsMobileAccountMenuOpen(false);
  };

  const handleMobileChatSelect = (chat) => {
    handleChatSelect(chat);
    handleMobileViewChange('chat');
  };

  const handleMobileAccountToggle = () => {
    setIsMobileAccountMenuOpen((prev) => !prev);
  };

  const handleMobileLogout = async () => {
    await handleLogout();
    setIsMobilePanelOpen(false);
    setIsMobileAccountMenuOpen(false);
  };

  const mobilePanelContent = (
    <div className="mobile-panel-shell">
      <PanelHeader
        title={chatEnabled ? 'Menu' : 'Instruments'}
        onClose={() => {
          setIsMobilePanelOpen(false);
          setIsMobileAccountMenuOpen(false);
        }}
      />
      <InstrumentSearch
        instruments={INSTRUMENTS}
        autoCompleteOptions={autoCompleteOptions}
        selectedInstrument={selectedInstrument}
        selectedCategory={selectedCategory}
        onSelectInstrument={handleMobileInstrumentSelect}
        onSearch={setSearchQuery}
        onSearchActiveChange={setIsInstrumentSearchActive}
        isDarkMode={isDarkMode}
      />
      <div className="mobile-panel-body">
        {chatEnabled ? (
          <ChatHistoryList
            chats={recentChats}
            selectedChatId={selectedChatId}
            onChatSelect={handleMobileChatSelect}
            instruments={INSTRUMENTS}
            onDeleteChat={handleDeleteChat}
          />
        ) : (
          <InstrumentHistoryList
            instruments={recentInstruments}
            selectedInstrument={selectedInstrument}
            onInstrumentSelect={handleMobileInstrumentSelect}
          />
        )}
      </div>
      {isMobileAccountMenuOpen && (
        <div className="mobile-panel-account-menu">
          {isAdmin && (
            <button
              type="button"
              className="mobile-panel-account-action mobile-panel-account-action-admin"
              onClick={() => {
                setIsMobilePanelOpen(false);
                setIsMobileAccountMenuOpen(false);
                navigate('/admin');
              }}
            >
              <AdminPanelSettingsOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
              Admin Dashboard
            </button>
          )}
          {journalPageEnabled && (
            <button
              type="button"
              className="mobile-panel-account-action"
              onClick={() => {
                setIsMobilePanelOpen(false);
                setIsMobileAccountMenuOpen(false);
                navigate('/trader-journal');
              }}
            >
              Trader Journal
            </button>
          )}
          <button
            type="button"
            className="mobile-panel-account-action"
            onClick={toggleTheme}
          >
            {isDarkMode ? 'Light Mode' : 'Dark Mode'}
          </button>
          <button
            type="button"
            className="mobile-panel-account-action"
            onClick={() => {
              setIsMobilePanelOpen(false);
              setIsMobileAccountMenuOpen(false);
              setIsRulesHelpVisible(true);
            }}
          >
            Validation Rules
          </button>
          <button
            type="button"
            className="mobile-panel-account-action"
            onClick={() => {
              setIsMobilePanelOpen(false);
              setIsMobileAccountMenuOpen(false);
              window.open('https://nanotradingcopilot.com/docs/', '_blank');
            }}
          >
            Help & Support
          </button>
          <button
            type="button"
            className="mobile-panel-account-action logout"
            onClick={handleMobileLogout}
          >
            Log out
          </button>
        </div>
      )}
      <div className="mobile-panel-footer">
        <button
          type="button"
          className="mobile-panel-account"
          onClick={handleMobileAccountToggle}
          aria-label="Account menu"
          aria-expanded={isMobileAccountMenuOpen}
        >
          <div className="mobile-panel-avatar">
            {userInfo?.username?.[0]?.toUpperCase() || 'U'}
          </div>
          <div className="mobile-panel-account-meta">
            <span className="mobile-panel-account-name">
              {userInfo?.username || 'User'}
            </span>
            <span className="mobile-panel-account-plan">
              {subscriptionStatus?.tierName || (userInfo?.premium ? 'Plus' : 'Free')}
            </span>
          </div>
        </button>
        <button
          type="button"
          className="mobile-panel-settings"
          onClick={handleMobileAccountToggle}
          aria-label="Settings"
          aria-expanded={isMobileAccountMenuOpen}
        >
          <SettingsOutlinedIcon fontSize="small" />
        </button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className={`ntb-chat-container ${isDarkMode ? 'dark-mode' : ''}`}>
        <MobileView
          isDarkMode={isDarkMode}
          title={mobileHeaderTitle}
          isPanelOpen={isMobilePanelOpen}
          onPanelOpen={() => setIsMobilePanelOpen(true)}
          onPanelClose={() => {
            setIsMobilePanelOpen(false);
            setIsMobileAccountMenuOpen(false);
          }}
          onTitleClick={() => setIsMobilePanelOpen(true)}
          activeView={activeTab === 'chat' ? 'chat' : activeTab === 'validate' ? 'validate' : 'graph'}
          onViewChange={handleMobileViewChange}
          onPrimaryAction={null}
          onSecondaryAction={null}
          panelContent={mobilePanelContent}
          userInfo={userInfo}
          onUserClick={handleMobileAccountToggle}
        >
          {activeTab === 'chat' && chatPanelVisible ? (
            <Chat
              auth={auth}
              isDarkMode={isDarkMode}
              selectedChatId={selectedChatId}
              selectedInstrument={selectedInstrument}
              selectedCategory={selectedCategory}
              isChatExpanded
              onChatToggle={toggleChat}
              addLine={addLine}
              onDisabledInputClick={handleDisabledInputClick}
              onDetach={handleDetachChat}
              isDetached={false}
            />
          ) : activeTab === 'validate' ? (
            historyViewVisible ? (
              <div className="mobile-history-container" style={{ height: '100%', overflow: 'hidden' }}>
                <ValidationHistoryView
                  isDarkMode={isDarkMode}
                  onBack={hideHistoryView}
                  onApplyTrade={(tradeData) => {
                    if (tradeData.instrument) {
                      handleInstrumentSelect(tradeData.instrument, selectedCategory);
                    }
                    if (setTradeSpec) {
                      setTradeSpec({
                        instrument: tradeData.instrument,
                        direction: tradeData.direction,
                        entryPrice: tradeData.entryPrice,
                        stopPrice: tradeData.stopPrice,
                        targetPrice: tradeData.targetPrice,
                        positionSize: tradeData.positionSize,
                      });
                    }
                  }}
                />
              </div>
            ) : (
              <div className="mobile-conceptgate-container">
                <ConceptGatePanel
                  isDarkMode={isDarkMode}
                  onTogglePanel={() => setActiveTab('graph')}
                  selectedInstrument={selectedInstrument}
                  selectedCategory={selectedCategory}
                  currentPrice={chartCurrentPrice}
                  onShowHistory={showHistoryView}
                />
              </div>
            )
          ) : (
            <div className="mobile-chart-container">
              <MobileGateOverlay
                onNavigateToGate={() => setActiveTab('validate')}
                selectedInstrument={selectedInstrument}
                selectedCategory={selectedCategory}
              />
              <MiddlePanel
                isDarkMode={isDarkMode}
                selectedCategory={selectedCategory}
                selectedInstrument={selectedInstrument}
                selectedChatId={selectedChatId}
                isChatExpanded={false}
                isExpandedLeftPanel={false}
                disableQuickStart={true}
                ref={chartRef}
              />
            </div>
          )}
        </MobileView>
        {chatPanelVisible && layoutState.rightPanel.detached && (
          <FloatingChatWindow
            position={{
              x: layoutState.rightPanel.floatingPosition.x ?? (window.innerWidth - (layoutState.rightPanel.floatingPosition.width || 400) - 20),
              y: layoutState.rightPanel.floatingPosition.y ?? 100
            }}
            size={{
              width: layoutState.rightPanel.floatingPosition.width || 400,
              height: layoutState.rightPanel.floatingPosition.height || 600
            }}
            onPositionChange={handleFloatingPositionChange}
            onSizeChange={handleFloatingSizeChange}
            onDock={handleDockChat}
            isDarkMode={isDarkMode}
            minWidth={300}
            minHeight={400}
            maxWidth={800}
          >
            <Chat
              auth={auth}
              isDarkMode={isDarkMode}
              selectedChatId={selectedChatId}
              selectedInstrument={selectedInstrument}
              selectedCategory={selectedCategory}
              isChatExpanded={true}
              onChatToggle={toggleChat}
              addLine={addLine}
              onDisabledInputClick={handleDisabledInputClick}
              onDetach={null}
              isDetached={true}
            />
          </FloatingChatWindow>
        )}
      </div>
    );
  }

  return (
    <div className={`ntb-chat-container ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`panels-container mobile-tab-menu ${getMobileTabClass()} ${panelState.collapsed ? 'left-panel-collapsed' : ''} ${!panelState.visible ? 'left-panel-hidden' : ''}`}>
        {panelState.visible && (
          <LeftPanel
            isOpen={isExpandedLeftPanel || activeTab === 'menu'}
            onTogglePanel={togglePanel}
            recentChats={recentChats}
            recentInstruments={recentInstruments}
            chatEnabled={chatEnabled}
            onChatSelect={handleChatSelect}
            onInstrumentSelect={handleInstrumentSelect}
            selectedChatId={selectedChatId}
            selectedInstrument={selectedInstrument}
            auth={auth}
            onChatsUpdated={() => {
              fetchRecentChats();
            }}
            instruments={INSTRUMENTS}
          />
        )}

        <div className="main-content">
          <Header
            isDarkMode={isDarkMode}
            isPremium={userInfo?.premium || userInfo?.admin}
            selectedCategory={selectedCategory}
            selectedInstrument={selectedInstrument}
            instruments={INSTRUMENTS}
            onCategoryChange={handleCategoryChange}
            onInstrumentChange={handleInstrumentSelect}
            searchQuery={searchQuery}
            autoCompleteOptions={autoCompleteOptions}
            onSelect={handleInstrumentSelect}
            onSearch={setSearchQuery}
            onSearchActiveChange={setIsInstrumentSearchActive}
            isInstrumentLoading={isInstrumentLoading}
            isCategoryLoading={isCategoryLoading}
            shouldHighlightInstrumentSelector={shouldHighlightInstrumentSelector}
            isPanelsSwitched={isPanelsSwitched}
            onTogglePanelsSwitch={togglePanelsSwitch}
            conceptGatePanelVisible={conceptGatePanelVisible}
            onToggleConceptGatePanel={toggleConceptGatePanel}
            chatPanelVisible={chatPanelVisible}
            onToggleChatPanel={toggleChatPanel}
            chatEnabled={chatEnabled}
            // User menu props
            onDarkModeChange={toggleTheme}
            onLogout={handleLogout}
            onShowRulesHelp={() => setIsRulesHelpVisible(true)}
            onStartTour={() => {
              if (onboardingTourRef.current) {
                onboardingTourRef.current.startTour();
              }
            }}
            onToggleJournal={toggleJournal}
          />

          <div className={`content-container ${isPanelsSwitched ? 'panels-switched' : ''}`}>
            {!isPanelsSwitched ? (
              <>
                {historyViewVisible ? (
                  <div className="middle-panel" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <ValidationHistoryView
                      isDarkMode={isDarkMode}
                      onBack={hideHistoryView}
                      onApplyTrade={(tradeData) => {
                        // Set instrument first
                        if (tradeData.instrument) {
                          handleInstrumentSelect(tradeData.instrument, selectedCategory);
                        }
                        // Apply trade spec via ConceptGate context
                        if (setTradeSpec) {
                          setTradeSpec({
                            instrument: tradeData.instrument,
                            direction: tradeData.direction,
                            entryPrice: tradeData.entryPrice,
                            stopPrice: tradeData.stopPrice,
                            targetPrice: tradeData.targetPrice,
                            positionSize: tradeData.positionSize,
                          });
                        }
                      }}
                    />
                  </div>
                ) : journalVisible ? (
                  <div className="middle-panel" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <TraderJournalView isEmbedded={true} onClose={() => setJournalVisible(false)} />
                  </div>
                ) : (
                  <MiddlePanel
                    isDarkMode={isDarkMode}
                    selectedCategory={selectedCategory}
                    selectedInstrument={selectedInstrument}
                    selectedChatId={selectedChatId}
                    isChatExpanded={isChatExpanded}
                    isExpandedLeftPanel={isExpandedLeftPanel}
                    onPriceUpdate={setChartCurrentPrice}
                    ref={chartRef}
                    onStartQuickTour={() => {
                      if (onboardingTourRef.current) {
                        onboardingTourRef.current.startTour();
                      }
                    }}
                  />
                )}

                {/* ConceptGate Panel */}
                {conceptGatePanelVisible && (
                  <div
                    className={`conceptgate-panel ${isDarkMode ? 'dark-mode' : ''}`}
                    style={{ width: `${conceptGatePanelWidth}px` }}
                  >
                    <ResizeHandle
                      onResize={setConceptGatePanelWidth}
                      minWidth={200}
                      maxWidth={500}
                      isPanelsSwitched={isPanelsSwitched}
                    />
                    <ConceptGatePanel
                      isDarkMode={isDarkMode}
                      onTogglePanel={toggleConceptGatePanel}
                      selectedInstrument={selectedInstrument}
                      selectedCategory={selectedCategory}
                      currentPrice={chartCurrentPrice}
                      onShowJournal={toggleJournal}
                      onShowHistory={showHistoryView}
                    />
                  </div>
                )}

                {chatPanelVisible && !layoutState.rightPanel.detached && (
                  <div
                    className={`right-panel ${isDarkMode ? 'dark-mode' : ''}`}
                    style={{ width: `${layoutState.rightPanel.width}px` }}
                  >
                    <ResizeHandle
                      onResize={setRightPanelWidth}
                      minWidth={250}
                      maxWidth={800}
                      isPanelsSwitched={isPanelsSwitched}
                    />
                    <Chat
                      auth={auth}
                      isDarkMode={isDarkMode}
                      selectedChatId={selectedChatId}
                      selectedInstrument={selectedInstrument}
                      selectedCategory={selectedCategory}
                      isChatExpanded={isChatExpanded || activeTab === 'chat'}
                      onChatToggle={toggleChat}
                      addLine={addLine}
                      onDisabledInputClick={handleDisabledInputClick}
                      onDetach={handleDetachChat}
                      isDetached={false}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                {chatPanelVisible && !layoutState.rightPanel.detached && (
                  <div
                    className={`right-panel chat-panel ${isDarkMode ? 'dark-mode' : ''}`}
                    style={{ width: `${layoutState.rightPanel.width}px` }}
                  >
                    <ResizeHandle
                      onResize={setRightPanelWidth}
                      minWidth={250}
                      maxWidth={800}
                      isPanelsSwitched={isPanelsSwitched}
                    />
                    <Chat
                      auth={auth}
                      isDarkMode={isDarkMode}
                      selectedChatId={selectedChatId}
                      selectedInstrument={selectedInstrument}
                      selectedCategory={selectedCategory}
                      isChatExpanded={isChatExpanded || activeTab === 'chat'}
                      onChatToggle={toggleChat}
                      addLine={addLine}
                      onDisabledInputClick={handleDisabledInputClick}
                      onDetach={handleDetachChat}
                      isDetached={false}
                    />
                  </div>
                )}

                {/* ConceptGate Panel (switched layout) */}
                {conceptGatePanelVisible && (
                  <div
                    className={`conceptgate-panel ${isDarkMode ? 'dark-mode' : ''}`}
                    style={{ width: `${conceptGatePanelWidth}px` }}
                  >
                    <ResizeHandle
                      onResize={setConceptGatePanelWidth}
                      minWidth={200}
                      maxWidth={500}
                      isPanelsSwitched={isPanelsSwitched}
                    />
                    <ConceptGatePanel
                      isDarkMode={isDarkMode}
                      onTogglePanel={toggleConceptGatePanel}
                      selectedInstrument={selectedInstrument}
                      selectedCategory={selectedCategory}
                      currentPrice={chartCurrentPrice}
                      onShowJournal={toggleJournal}
                      onShowHistory={showHistoryView}
                    />
                  </div>
                )}

                {historyViewVisible ? (
                  <div className="middle-panel" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <ValidationHistoryView
                      isDarkMode={isDarkMode}
                      onBack={hideHistoryView}
                      onApplyTrade={(tradeData) => {
                        if (tradeData.instrument) {
                          handleInstrumentSelect(tradeData.instrument, selectedCategory);
                        }
                        if (setTradeSpec) {
                          setTradeSpec({
                            instrument: tradeData.instrument,
                            direction: tradeData.direction,
                            entryPrice: tradeData.entryPrice,
                            stopPrice: tradeData.stopPrice,
                            targetPrice: tradeData.targetPrice,
                            positionSize: tradeData.positionSize,
                          });
                        }
                      }}
                    />
                  </div>
                ) : journalVisible ? (
                  <div className="middle-panel" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <TraderJournalView isEmbedded={true} onClose={() => setJournalVisible(false)} />
                  </div>
                ) : (
                  <MiddlePanel
                    isDarkMode={isDarkMode}
                    selectedCategory={selectedCategory}
                    selectedInstrument={selectedInstrument}
                    selectedChatId={selectedChatId}
                    isChatExpanded={isChatExpanded}
                    isExpandedLeftPanel={isExpandedLeftPanel}
                    onPriceUpdate={setChartCurrentPrice}
                    ref={chartRef}
                    onStartQuickTour={() => {
                      if (onboardingTourRef.current) {
                        onboardingTourRef.current.startTour();
                      }
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      {!isMobile && (
        <OnboardingTour
          ref={onboardingTourRef}
          darkMode={isDarkMode}
          onCategoryChange={handleCategoryChange}
          onInstrumentChange={handleInstrumentSelect}
          selectedCategory={selectedCategory}
          selectedInstrument={selectedInstrument}
        />
      )}
      {/* Floating Chat Window - Rendered outside panels-container to avoid layout interference */}
      {chatPanelVisible && layoutState.rightPanel.detached && (
        <FloatingChatWindow
          position={{
            x: layoutState.rightPanel.floatingPosition.x ?? (window.innerWidth - (layoutState.rightPanel.floatingPosition.width || 400) - 20),
            y: layoutState.rightPanel.floatingPosition.y ?? 100
          }}
          size={{
            width: layoutState.rightPanel.floatingPosition.width || 400,
            height: layoutState.rightPanel.floatingPosition.height || 600
          }}
          onPositionChange={handleFloatingPositionChange}
          onSizeChange={handleFloatingSizeChange}
          onDock={handleDockChat}
          isDarkMode={isDarkMode}
          minWidth={300}
          minHeight={400}
          maxWidth={800}
        >
          <Chat
            auth={auth}
            isDarkMode={isDarkMode}
            selectedChatId={selectedChatId}
            selectedInstrument={selectedInstrument}
            selectedCategory={selectedCategory}
            isChatExpanded={true}
            onChatToggle={toggleChat}
            addLine={addLine}
            onDisabledInputClick={handleDisabledInputClick}
            onDetach={null}
            isDetached={true}
          />
        </FloatingChatWindow>
      )}
      <KeyboardShortcutsHelp
        isDarkMode={isDarkMode}
        isVisible={isKeyboardShortcutsVisible}
        onClose={(visible) => setIsKeyboardShortcutsVisible(visible)}
      />
      <RulesHelpModal
        isDarkMode={isDarkMode}
        isVisible={isRulesHelpVisible}
        onClose={() => setIsRulesHelpVisible(false)}
      />
    </div>
  );
};

export default NTBChatView;
